import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers';
import * as api from '../api/pins';

const pinKeys = {
  mine: (userId: string) => ['pins', 'mine', userId] as const,
  forMessages: (userId: string, key: string) => ['pins', 'ids', userId, key] as const,
};

/** Every message the current user has pinned, newest first. */
export function useMyPins() {
  const { user } = useAuth();
  return useQuery({
    queryKey: pinKeys.mine(user?.id ?? ''),
    queryFn: () => api.listMyPins(user!.id),
    enabled: Boolean(user?.id),
  });
}

/** Set of message ids (within `messageIds`) the user has pinned. */
export function usePinnedIds(messageIds: string[]) {
  const { user } = useAuth();
  const key = [...messageIds].sort().join(',');
  return useQuery({
    queryKey: pinKeys.forMessages(user?.id ?? '', key),
    queryFn: () => api.listPinnedIdsForMessages(user!.id, messageIds),
    enabled: Boolean(user?.id) && messageIds.length > 0,
  });
}

/** Pin / unpin mutations, both invalidating the pin caches on success. */
export function usePinActions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['pins'] });

  const pin = useMutation({
    mutationFn: (messageId: string) => api.pinMessage(user!.id, messageId),
    onSuccess: invalidate,
  });
  const unpin = useMutation({
    mutationFn: (messageId: string) => api.unpinMessage(user!.id, messageId),
    onSuccess: invalidate,
  });
  return { pin, unpin };
}
